import type { RecordModel } from "pocketbase";
import { pb, authHeader } from "./client.js";
import type { Role } from "../lib/roles.js";

// ── Custom-route response/request types (match the Go handlers exactly) ──────────

export interface SearchItem {
  package: string; // "@scope/name"
  scope: string; // "@scope"
  name: string;
  kind: string;
  team: string;
  title: string;
  description: string;
  tags: string[];
  category: string;
  visibility: "public" | "private";
  latestVersion: string;
  tags_dist: Record<string, string>; // tag name -> version
  hasStableTag: boolean;
  pulls30d: number;
  pullsTotal: number;
  updatedAt: string;
  score?: number;
}

export interface FacetCount {
  value: string;
  count: number;
}

export interface Facets {
  kind: FacetCount[];
  tag: FacetCount[];
  scope: FacetCount[];
  category: FacetCount[];
  hasStableTag: FacetCount[];
}

export interface SearchResponse {
  page: number;
  perPage: number;
  totalItems: number;
  totalPages: number;
  query: unknown;
  items: SearchItem[];
  facets?: Facets;
}

export type SortKey = "relevance" | "pulls" | "updated" | "name";

export interface SearchParams {
  q?: string;
  kind?: string[];
  tag?: string[];
  scope?: string[];
  category?: string[];
  stable?: boolean;
  sort?: SortKey;
  page?: number;
  perPage?: number;
  facets?: boolean;
  archived?: boolean;
}

export interface ResolveResponse {
  package: string;
  tag?: string; // present only when ref was a tag (omitted on a version pin)
  version: string;
  kind: string;
  digest: string;
  dependencies: string[]; // "@scope/name@ref"
  downloadUrl: string;
}

export interface DownloadResult {
  yaml: string;
  digest: string; // from the ETag header, unquoted
}

export interface TagResult {
  name: string;
  version: string;
  managed: boolean;
  isDefault: boolean;
}

/** A version-scoped binary attachment (images for Asset, csv/xlsx/parquet for
 * DataSource kinds) — immutable once its version publishes. Served by custom
 * routes, not auto-REST (List/View rules are locked so visibility masking
 * applies). No "updated" field: resources are append-only, like the version
 * they belong to. */
export interface ResourceRecord {
  id: string;
  package: string;
  name: string;
  content_hash: string;
  size: number;
  mime_type: string;
  created: string;
}

// ── Auto-REST record types (PocketBase collections) ─────────────────────────────

export interface ScopeRecord extends RecordModel {
  name: string;
  team: string;
  expand?: { team?: TeamRecord };
}

export interface TeamRecord extends RecordModel {
  name: string;
  display_name: string;
  /** The owning user's id for personal (signup-created) teams, else "". */
  personal_for: string;
}

export interface TeamMemberRecord extends RecordModel {
  user: string;
  team: string;
  role: Role;
  expand?: { user?: RecordModel; team?: TeamRecord };
}

export interface VersionRecord extends RecordModel {
  package: string;
  version: string;
  digest: string;
  document: unknown;
  kind: string;
  api_version: string;
  compat: string;
  dependencies: unknown;
  deprecated: boolean;
  deprecatedMsg: string;
  yanked: boolean;
  yankedMsg: string;
  published_by: string;
}

export interface PackageRecord extends RecordModel {
  scope: string;
  name: string;
  kind: string;
  default_tag: string;
  title: string;
  description: string;
  tags: string;
  category: string;
  visibility: string;
  latest_version: string;
  deprecated: boolean;
  deprecatedMsg: string;
  archived: boolean;
  expand?: {
    scope?: ScopeRecord;
    latest_version?: VersionRecord;
  };
}

export interface TagRecord extends RecordModel {
  package: string;
  name: string;
  version: string;
  managed: boolean;
  expand?: { version?: VersionRecord };
}

// ── Custom routes ───────────────────────────────────────────────────────────────

const BASE = "/api/registry";

/** sendJSON wraps fetch so non-2xx bodies throw the canonical {status,response}. */
export async function sendJSON<T>(path: string, init: RequestInit): Promise<T> {
  const res = await fetch(path, init);
  if (!res.ok) {
    let body: unknown = {};
    try {
      body = await res.json();
    } catch {
      /* non-JSON error body */
    }
    throw { status: res.status, response: body };
  }
  return (await res.json()) as T;
}

function buildSearchQuery(p: SearchParams): string {
  const q = new URLSearchParams();
  if (p.q) q.set("q", p.q);
  for (const v of p.kind ?? []) q.append("kind", v);
  for (const v of p.tag ?? []) q.append("tag", v);
  for (const v of p.scope ?? []) q.append("scope", v);
  for (const v of p.category ?? []) q.append("category", v);
  if (p.stable) q.set("stable", "1");
  if (p.sort) q.set("sort", p.sort);
  if (p.page) q.set("page", String(p.page));
  if (p.perPage) q.set("perPage", String(Math.min(p.perPage, 100)));
  if (p.facets !== undefined) q.set("facets", p.facets ? "1" : "0");
  if (p.archived) q.set("archived", "1");
  return q.toString();
}

/**
 * search calls GET /api/registry/search (R8). Anonymous for public packages;
 * the bearer token is attached (when present) so members/maintainers also see
 * their team's private and archived packages in the results.
 */
export function search(params: SearchParams): Promise<SearchResponse> {
  return sendJSON<SearchResponse>(`${BASE}/search?${buildSearchQuery(params)}`, {
    method: "GET",
    headers: { ...authHeader() },
  });
}

/**
 * resolve calls GET /api/registry/resolve/{scope}/{name}?ref= (scope/name WITHOUT '@').
 * Anonymous for public packages; the bearer token is attached so viewers can also
 * resolve packages in a private scope they belong to (otherwise the server masks
 * them as package_not_found, R11/existence-masking).
 */
export function resolve(scope: string, name: string, ref?: string): Promise<ResolveResponse> {
  const q = ref ? `?ref=${encodeURIComponent(ref)}` : "";
  return sendJSON<ResolveResponse>(
    `${BASE}/resolve/${encodeURIComponent(scope)}/${encodeURIComponent(name)}${q}`,
    { method: "GET", headers: { ...authHeader() } },
  );
}

/**
 * download fetches the exact YAML bytes + digest (from the ETag) for a version (R11).
 * The bearer token is attached (same reasoning as resolve) so private packages are
 * readable by their team's members, not just anonymous callers of public ones.
 */
export async function download(
  scope: string,
  name: string,
  version: string,
): Promise<DownloadResult> {
  const res = await fetch(
    `${BASE}/download/${encodeURIComponent(scope)}/${encodeURIComponent(name)}/${encodeURIComponent(version)}`,
    { method: "GET", headers: { ...authHeader() } },
  );
  if (!res.ok) {
    let body: unknown = {};
    try {
      body = await res.json();
    } catch {
      /* ignore */
    }
    throw { status: res.status, response: body };
  }
  const etag = res.headers.get("ETag") ?? "";
  return { yaml: await res.text(), digest: etag.replace(/^"|"$/g, "") };
}

/**
 * tag moves a non-`latest` tag to a version (R17). `packageRecordId` is the
 * PocketBase packages record id; `version` is the SEMVER label (e.g. "1.2.0"),
 * NOT a record id.
 */
export function tag(
  packageRecordId: string,
  name: string,
  version: string,
): Promise<TagResult> {
  return sendJSON<TagResult>(`${BASE}/tag`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeader() },
    body: JSON.stringify({ package: packageRecordId, name, version }),
  });
}

/** deprecate flags/unflags a version (maintainer+). `versionRecordId` is a versions record id. */
export function deprecate(
  versionRecordId: string,
  message: string,
  undo = false,
): Promise<{ status: string }> {
  return sendJSON(`${BASE}/deprecate`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeader() },
    body: JSON.stringify({ version: versionRecordId, message, undo }),
  });
}

/**
 * setVisibility flips a package public/private (maintainer+) via
 * PATCH /api/registry/packages/{scope}/{name}/settings.
 */
export function setVisibility(
  scope: string,
  name: string,
  visibility: "public" | "private",
): Promise<{ visibility: string }> {
  return sendJSON(
    `${BASE}/packages/${encodeURIComponent(scope)}/${encodeURIComponent(name)}/settings`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...authHeader() },
      body: JSON.stringify({ visibility }),
    },
  );
}

/**
 * deprecatePackage flags/unflags a whole package (all versions, maintainer+) via
 * POST /api/registry/packages/{scope}/{name}/deprecate.
 */
export function deprecatePackage(
  scope: string,
  name: string,
  message: string,
  undo = false,
): Promise<{ deprecated: boolean }> {
  return sendJSON(
    `${BASE}/packages/${encodeURIComponent(scope)}/${encodeURIComponent(name)}/deprecate`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeader() },
      body: JSON.stringify({ message, undo }),
    },
  );
}

/**
 * archivePackage soft-removes/restores a whole package (maintainer+) via
 * POST /api/registry/packages/{scope}/{name}/archive. An archived package is
 * hidden from search/browse and blocked from downloads; version rows are kept.
 */
export function archivePackage(
  scope: string,
  name: string,
  undo = false,
): Promise<{ archived: boolean }> {
  return sendJSON(
    `${BASE}/packages/${encodeURIComponent(scope)}/${encodeURIComponent(name)}/archive`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeader() },
      body: JSON.stringify({ undo }),
    },
  );
}

/** yank flags/unflags a version (maintainer+). `versionRecordId` is a versions record id. */
export function yank(
  versionRecordId: string,
  message: string,
  undo = false,
): Promise<{ status: string }> {
  return sendJSON(`${BASE}/yank`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeader() },
    body: JSON.stringify({ version: versionRecordId, message, undo }),
  });
}

/**
 * listResources calls GET /api/registry/resources/{scope}/{name}/{version}:
 * visibility-masked, anonymous for public packages (same reasoning as
 * resolve/download). version must be an exact resolved label, not a tag —
 * callers resolve "latest" via resolve() first, same as the Definition tab.
 */
export function listResources(scope: string, name: string, version: string): Promise<ResourceRecord[]> {
  return sendJSON<ResourceRecord[]>(
    `${BASE}/resources/${encodeURIComponent(scope)}/${encodeURIComponent(name)}/${encodeURIComponent(version)}`,
    { method: "GET", headers: { ...authHeader() } },
  );
}

/**
 * fetchResourceBlob fetches a resource's bytes with the bearer token attached
 * (private packages need auth; a plain <img src> can't carry a header) so the
 * caller can build an object URL for a Download action.
 */
export async function fetchResourceBlob(
  scope: string,
  name: string,
  version: string,
  resourceName: string,
): Promise<Blob> {
  const res = await fetch(
    `${BASE}/resources/${encodeURIComponent(scope)}/${encodeURIComponent(name)}/${encodeURIComponent(version)}/${encodeURIComponent(resourceName)}`,
    { method: "GET", headers: { ...authHeader() } },
  );
  if (!res.ok) {
    let body: unknown = {};
    try {
      body = await res.json();
    } catch {
      /* non-JSON error body */
    }
    throw { status: res.status, response: body };
  }
  return res.blob();
}

// ── Auto-REST reads ─────────────────────────────────────────────────────────────

/** getPackage loads a package by @scope/name with its scope, team, and latest version expanded. */
export function getPackage(scope: string, name: string): Promise<PackageRecord> {
  return pb
    .collection("packages")
    .getFirstListItem<PackageRecord>(
      pb.filter("scope.name = {:s} && name = {:n}", { s: scope, n: name }),
      { expand: "scope,scope.team,latest_version" },
    );
}

export interface Availability {
  available: boolean;
  /** The existing package when taken (scope.team expanded so callers can tell "yours"). */
  existing?: PackageRecord;
}

/** checkAvailability probes @scope/name via getPackage: 404 ⇒ available. */
export async function checkAvailability(scope: string, name: string): Promise<Availability> {
  try {
    return { available: false, existing: await getPackage(scope, name) };
  } catch (e) {
    if ((e as { status?: number } | null)?.status === 404) return { available: true };
    throw e;
  }
}

/** listVersions returns every version of a package, newest first. */
export function listVersions(packageId: string): Promise<VersionRecord[]> {
  return pb.collection("versions").getFullList<VersionRecord>({
    filter: pb.filter("package = {:p}", { p: packageId }),
    sort: "-created",
  });
}

/** listTags returns every tag of a package with its target version expanded. */
export function listTags(packageId: string): Promise<TagRecord[]> {
  return pb.collection("tags").getFullList<TagRecord>({
    filter: pb.filter("package = {:p}", { p: packageId }),
    sort: "name",
    expand: "version",
  });
}
